<script setup>
const { t } = useI18n();
const PALETTE = {
    primary: '#1473E6',
    success: '#16A34A',
    warning: '#F59E0B',
    danger: '#EF4444',
    neutral: '#64748B'
};

const props = defineProps({
    modelValue: { type: Boolean, default: true },
    title: { type: String, default: '' },
    message: { type: String, default: '' },

    variant: { type: String, default: 'primary' },
    color: { type: String, default: '' },
    gradient: { type: Boolean, default: true },
    pill: { type: Boolean, default: true },
    dense: { type: Boolean, default: false },
    shadow: { type: Boolean, default: false },
    divider: { type: Boolean, default: true },

    actions: { type: Array, default: () => [] },
    showIcon: { type: Boolean, default: true },
    closable: { type: Boolean, default: true },
    closeOnEsc: { type: Boolean, default: true }
});

const emit = defineEmits(['update:modelValue', 'close', 'action']);
const isShown = ref(props.modelValue);

watch(
    () => props.modelValue,
    (v) => (isShown.value = v)
);
watch(isShown, (v) => emit('update:modelValue', v));

const hasAnyRight = computed(() => props.closable || props.actions.length > 0);

const onAction = (action, index) => emit('action', { action, index, key: action?.key ?? null });
const close = () => {
    isShown.value = false;
    emit('close');
};
const onKeydown = (e) => {
    if (props.closeOnEsc && e.key === 'Escape') close();
};
onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => document.removeEventListener('keydown', onKeydown));

const btnClass = (variant) => {
    switch (variant) {
        case 'primary':
            return 'alert-btn-primary';
        case 'danger':
            return 'alert-btn-danger';
        case 'text':
            return 'alert-btn-text';
        default:
            return 'alert-btn-default';
    }
};

const hexToRgb = (hex) => {
    let h = hex.replace('#', '').trim();
    if (h.length === 3)
        h = h
            .split('')
            .map((c) => c + c)
            .join('');
    const num = parseInt(h, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHex = (r, g, b) => {
    const to = (v) => ('0' + v.toString(16)).slice(-2);
    return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
};

const shade = (hex, percent = 0) => {
    const { r, g, b } = hexToRgb(hex);
    const t = percent < 0 ? 0 : 255;
    const p = Math.abs(percent) / 100;
    const R = Math.round((t - r) * p + r);
    const G = Math.round((t - g) * p + g);
    const B = Math.round((t - b) * p + b);
    return rgbToHex(R, G, B);
};

const bestTextOn = (bgHex) => {
    const { r, g, b } = hexToRgb(bgHex);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 160 ? '#111827' : '#FFFFFF';
};

const cssVars = computed(() => {
    const base = (props.color && props.color.trim()) || PALETTE[props.variant] || PALETTE.primary;
    const top = props.gradient ? shade(base, +6) : base;
    const bot = props.gradient ? shade(base, -4) : base;
    const text = bestTextOn(bot);
    return {
        '--alert-bg-1': top,
        '--alert-bg-2': bot,
        '--alert-fg': text,
        '--alert-brand': base
    };
});
</script>

<template>
    <div v-if="isShown" class="alert" :class="[
        `alert-${variant}`,
        pill ? 'alert-pill' : '',
        dense ? 'alert-dense' : '',
        shadow ? 'alert-shadow' : '',
        gradient ? 'alert-gradient' : 'alert-flat'
    ]" :style="cssVars" role="alert" aria-live="polite">
        <div class="alert-left">
            <div v-if="showIcon" class="alert-icon" aria-hidden="true">
                <AlertCircleFilledIcon />
            </div>

            <div class="alert-content">
                <p class="alert-text">
                    <strong v-if="title">{{ t(title) }}</strong>
                    <span v-if="title && (message || $slots.default)">&nbsp;—&nbsp;</span>
                    <span v-if="message">{{ t(message) }}</span>
                    <slot />
                </p>
            </div>
        </div>

        <div class="alert-right" :class="{ 'alert-right--has-divider': divider && hasAnyRight }">
            <span v-if="divider && hasAnyRight" class="alert-divider" aria-hidden="true"></span>

            <div v-if="actions.length || $slots.actions" class="alert-actions">
                <slot name="actions">
                    <button v-for="(a, i) in actions" :key="a.key ?? i" type="button"
                        :class="['alert-btn', 'alert-btn-sm', 'alert-btn-pill', btnClass(a.variant)]"
                        @click="onAction(a, i)">
                        {{ t(a.label) }}
                    </button>
                </slot>
            </div>

            <button v-if="closable" class="alert-close" type="button" aria-label="Close alert" @click="close">×</button>
        </div>
    </div>
</template>